/**
 * Orquestra remoto + cache local da lista por `user_id`.
 */

import type { Payment } from '@/core/domain';
import type {
  ListPaymentsByUserOptions,
  PaymentDocSubscriptionCallback,
  PaymentDocSubscriptionErrorCallback,
  PaymentsRepository,
} from '../../domain/repositories/payments-repository';
import type { PaymentsRemoteDataSource } from '../datasources/payments-remote';
import type { PaymentsLocalDataSource } from '../datasources/payments-local';

export class PaymentsRepositoryImpl implements PaymentsRepository {
  constructor(
    private readonly remote: PaymentsRemoteDataSource,
    private readonly local: PaymentsLocalDataSource,
  ) {}

  async getAllByUserId(userId: string, options?: ListPaymentsByUserOptions): Promise<Payment[]> {
    if (!options?.forceRefresh) {
      const cached = await this.local.getListByUserId(userId);
      if (cached !== null) return cached;
    }
    const fromRemote = await this.remote.getAllByUserId(userId);
    await this.local.setListByUserId(userId, fromRemote).catch(() => {});
    return fromRemote;
  }

  async getById(id: string): Promise<Payment | null> {
    return this.remote.getById(id);
  }

  async create(payment: Payment): Promise<Payment> {
    const created = await this.remote.create(payment);
    await this.local.clearListByUserId(payment.user_id).catch(() => {});
    return created;
  }

  async update(id: string, data: Partial<Payment>): Promise<Payment> {
    const updated = await this.remote.update(id, data);
    await this.local.clearListByUserId(updated.user_id).catch(() => {});
    return updated;
  }

  async delete(id: string): Promise<void> {
    const before = await this.remote.getById(id);
    await this.remote.delete(id);
    if (before) await this.local.clearListByUserId(before.user_id).catch(() => {});
  }

  subscribeByIdForUser(
    paymentId: string,
    userId: string,
    onNext: PaymentDocSubscriptionCallback,
    onError: PaymentDocSubscriptionErrorCallback
  ): () => void {
    return this.remote.subscribeDocByIdForUser(paymentId, userId, onNext, onError);
  }
}
